/**
 * Workstream — cross-module follow-up app.
 *
 * Three tabs share one shell:
 *   1. Build  — pick items across modules, group by owner, AI-draft, send
 *   2. Inbox  — see what was sent, who acknowledged, re-nudge if needed
 *   3. Audit  — SOC 2 CC7.2 event log
 *
 * UX principles (kept honest from the spec):
 *   - "One owner, one digest" — kills lost-in-email syndrome.
 *   - Brief by construction: drafts capped at 5 sentences server-side.
 *   - Human-in-the-loop: no auto-send, ever. Every email is approved.
 *   - Personalized: per-account voice profile, built from approved sends.
 *
 * Theme: matches PSI's OracleHome. Reads from the same localStorage key
 * (`pom-theme`) and exposes a t(light, dark) helper to every sub-component
 * via React context. Defaults to LIGHT.
 *
 * Item picker UX: modules render collapsed by default (one row per module
 * with an item count). Click a module to expand and select individual
 * items. Solves the "too much information at once" complaint.
 */

import { useState, useEffect, useMemo, useCallback, useContext, createContext, useRef } from 'react';
import AppSidebar from '../../components/AppSidebar';

const API = process.env.REACT_APP_API_URL || '';
// No icons anywhere in Workstream — module rows are text-only (label +
// description + counts) per the professional, no-decoration house style.
// The server catalog still sends an `icon` field for other consumers; this
// page deliberately never renders it.

// ---------------------------------------------------------------------------
// Theme — mirrors OracleHome.useTheme so the look is identical
// ---------------------------------------------------------------------------

const ThemeContext = createContext({ theme: 'light', t: (l) => l });

function useThemeProvider() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return localStorage.getItem('pom-theme') || 'light';
  });
  useEffect(() => {
    const onStorage = (e) => { if (e.key === 'pom-theme' && e.newValue) setTheme(e.newValue); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const t = useCallback((light, dark) => (theme === 'dark' ? dark : light), [theme]);
  return { theme, t };
}

function useT() {
  return useContext(ThemeContext);
}

// ===========================================================================
// Shell
// ===========================================================================

export default function WorkstreamHome({ user, onSelectApp }) {
  const themeCtx = useThemeProvider();
  const { t } = themeCtx;
  const [tab, setTab] = useState('build');
  const [activeCart, setActiveCart] = useState(null);

  return (
    <ThemeContext.Provider value={themeCtx}>
      <div className={`flex flex-1 min-h-0 min-w-0 ${t('bg-slate-50', 'bg-[#0a0e14]')}`}>
        <AppSidebar selectedApp="workstream" onSelectApp={onSelectApp || (() => {})} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <div className="max-w-6xl mx-auto px-6 py-8">
            <Header tab={tab} setTab={setTab} />
            <MailConsentStrip user={user} />
            {tab === 'build' && <BuildView user={user} activeCart={activeCart} setActiveCart={setActiveCart} />}
            {tab === 'inbox' && <InboxView />}
            {tab === 'audit' && <AuditView />}
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

// ===========================================================================
// Header
// ===========================================================================

function Header({ tab, setTab }) {
  const { t } = useT();
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className={`text-2xl font-bold tracking-tight ${t('text-slate-900', 'text-white')}`}>Workstream</h1>
            <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 border ${t('text-emerald-700 bg-emerald-100 border-emerald-200', 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30')}`}>
              AI-assisted
            </span>
          </div>
          <p className={`text-sm mt-1 ${t('text-slate-600', 'text-slate-400')}`}>
            Pick items across every module. Group by owner. Send one clear nudge each.
          </p>
        </div>
      </div>

      <div className={`border-b flex gap-1 ${t('border-slate-200', 'border-slate-800')}`}>
        {[
          { id: 'build', label: 'Build follow-up' },
          { id: 'inbox', label: 'Inbox' },
          { id: 'audit', label: 'Audit log' },
        ].map((x) => (
          <button
            key={x.id}
            onClick={() => setTab(x.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition ${
              tab === x.id
                ? `${t('border-blue-600 text-slate-900', 'border-blue-500 text-white')}`
                : `border-transparent ${t('text-slate-500 hover:text-slate-700', 'text-slate-400 hover:text-slate-200')}`
            }`}
          >
            {x.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ===========================================================================
// Mail-consent strip
// ===========================================================================

function MailConsentStrip({ user }) {
  const { t } = useT();
  const [consent, setConsent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [popup, setPopup] = useState(null);

  // Only federated users (sign-in-with-Microsoft) can have PSI send AS
  // them via Microsoft Graph. Local-account users (PCG-managed password)
  // can't — their emails always go via PSI's ACS sender with a branded
  // "Maria · Acme Health Plan" display name. So we hide the strip for
  // them entirely: no false promise, no confusing "Connect" CTA.
  const isFederated = user?.auth_source === 'azure_ad';

  const refresh = useCallback(async () => {
    if (!isFederated) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/api/workstream/mail-consent`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setConsent(data.consent || null);
    } finally {
      setLoading(false);
    }
  }, [isFederated]);
  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    function handler(ev) {
      if (!ev?.data || ev.data.source !== 'workstream-mail-consent') return;
      if (popup) try { popup.close(); } catch {}
      setPopup(null);
      refresh();
    }
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [popup, refresh]);

  const startConnect = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/workstream/mail-consent/start`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success && data.authorize_url) {
        const win = window.open(data.authorize_url, 'psi-mail-consent', 'width=620,height=720,noopener=no');
        setPopup(win);
      }
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect your email? Workstream emails will go from PSI\'s noreply address instead of yours until you reconnect.')) return;
    setBusy(true);
    try {
      await fetch(`${API}/api/workstream/mail-consent`, {
        method: 'DELETE', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'user_initiated' }),
      });
      await refresh();
    } finally { setBusy(false); }
  };

  if (loading) return null;
  // Hide the entire strip for local-account users. They can't connect
  // Microsoft Graph (no Microsoft identity in our system) — surfacing a
  // "Connect my email" CTA they can't act on is just noise.
  if (!isFederated) return null;
  const status = consent?.status || 'not_connected';

  const containerCls = status === 'active'
    ? t('border-emerald-200 bg-emerald-50', 'border-emerald-500/30 bg-emerald-500/5')
    : status === 'error'
      ? t('border-red-200 bg-red-50', 'border-red-500/30 bg-red-500/5')
      : t('border-slate-200 bg-white', 'border-slate-700/50 bg-slate-900/40');

  return (
    <div className={`mb-6 rounded-2xl border p-4 flex items-start gap-4 ${containerCls}`}>
      <div className={`w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 ${t('bg-slate-50 border-slate-200', 'bg-gradient-to-br from-orange-500/20 to-amber-500/20 border-orange-500/30')}`}>
        <svg width="20" height="20" viewBox="0 0 23 23" aria-hidden="true">
          <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
          <rect x="12" y="1" width="10" height="10" fill="#7FBA00"/>
          <rect x="1" y="12" width="10" height="10" fill="#00A4EF"/>
          <rect x="12" y="12" width="10" height="10" fill="#FFB900"/>
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        {status === 'active' ? (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <p className={`text-sm font-semibold ${t('text-slate-900', 'text-white')}`}>
                Sending as <span className={t('text-emerald-700', 'text-emerald-300')}>{consent.graph_user_email || 'your email'}</span>
              </p>
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border ${t('bg-emerald-100 border-emerald-200 text-emerald-700', 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300')}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${t('bg-emerald-600', 'bg-emerald-400')}`} />Connected
              </span>
            </div>
            <p className={`text-xs mt-0.5 ${t('text-slate-600', 'text-slate-400')}`}>
              Workstream emails go from your Microsoft 365 address with a copy in your Sent Items. Recipients can reply directly to you.
              {consent.send_count > 0 && <> · <strong className={t('text-slate-800', 'text-slate-300')}>{consent.send_count}</strong> sent so far.</>}
            </p>
          </>
        ) : status === 'error' ? (
          <>
            <p className={`text-sm font-semibold ${t('text-red-800', 'text-red-200')}`}>Your email connection needs to be refreshed</p>
            <p className={`text-xs mt-0.5 ${t('text-red-700/80', 'text-red-200/80')}`}>Microsoft revoked the access (password change, MFA event, or admin action). Reconnect to keep sending from your address.</p>
          </>
        ) : (
          <>
            <p className={`text-sm font-semibold ${t('text-slate-900', 'text-white')}`}>Send Workstream emails from your own address</p>
            <p className={`text-xs mt-0.5 leading-relaxed ${t('text-slate-600', 'text-slate-400')}`}>
              One click connects your work Microsoft 365 mailbox so recipients see emails from <strong className={t('text-slate-800', 'text-slate-200')}>you</strong> instead of a noreply address.
              Sign in with your work account on your organization's registered email domain. Personal addresses such as Gmail or Outlook.com cannot be connected.
            </p>
          </>
        )}
      </div>
      <div className="flex-shrink-0">
        {status === 'active' ? (
          <button type="button" onClick={disconnect} disabled={busy}
            className={`px-3 py-1.5 border rounded-lg text-xs transition disabled:opacity-40 ${t('bg-white hover:bg-slate-50 border-slate-200 text-slate-700', 'bg-slate-800 hover:bg-slate-700 border-slate-700 text-slate-300')}`}>
            Disconnect
          </button>
        ) : (
          <button type="button" onClick={startConnect} disabled={busy}
            className="px-4 py-2 bg-gradient-to-r from-orange-600 to-amber-500 rounded-lg text-sm font-semibold text-white hover:shadow-lg hover:shadow-blue-500/25 disabled:opacity-40 transition whitespace-nowrap">
            {busy ? 'Opening…' : status === 'error' ? 'Reconnect' : 'Connect my email'}
          </button>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// BUILD VIEW
// ===========================================================================

function BuildView({ user, activeCart, setActiveCart }) {
  const [step, setStep] = useState(1);
  const [cart, setCart] = useState(activeCart);
  const [items, setItems] = useState([]);
  const [intent, setIntent] = useState('');
  const [digests, setDigests] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const refreshCart = useCallback(async (cartId) => {
    if (!cartId) return;
    const res = await fetch(`${API}/api/workstream/carts/${cartId}`, { credentials: 'include' });
    const data = await res.json();
    if (data.success) {
      setCart(data.cart);
      setItems(data.items || []);
      if (data.cart.intent) setIntent(data.cart.intent);
    }
  }, []);

  const ensureCart = useCallback(async () => {
    if (cart) return cart;
    const res = await fetch(`${API}/api/workstream/carts`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'cart create failed');
    setCart(data.cart);
    setActiveCart(data.cart);
    return data.cart;
  }, [cart, setActiveCart]);

  const addItems = useCallback(async (toAdd) => {
    setError(null);
    try {
      const c = await ensureCart();
      const res = await fetch(`${API}/api/workstream/carts/${c.id}/items`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: toAdd }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'add failed');
      await refreshCart(c.id);
    } catch (e) { setError(e.message); }
  }, [ensureCart, refreshCart]);

  const removeItem = useCallback(async (itemId) => {
    if (!cart) return;
    await fetch(`${API}/api/workstream/carts/${cart.id}/items/${itemId}`, { method: 'DELETE', credentials: 'include' });
    await refreshCart(cart.id);
  }, [cart, refreshCart]);

  const continueToGroup = useCallback(async () => {
    if (!cart) return;
    setBusy(true); setError(null);
    try {
      if (intent && intent !== cart.intent) {
        await fetch(`${API}/api/workstream/carts/${cart.id}`, {
          method: 'PATCH', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ intent }),
        });
      }
      const res = await fetch(`${API}/api/workstream/carts/${cart.id}/group`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'grouping failed');
      setDigests(data.digests);
      setStep(3);
      generateDrafts();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }, [cart, intent]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateDrafts = useCallback(async () => {
    if (!cart) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/workstream/carts/${cart.id}/draft`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intent }),
      });
      const data = await res.json();
      if (data.success) setDrafts(data.drafts);
    } finally { setBusy(false); }
  }, [cart, intent]);

  const regenerateDraft = useCallback(async (ownerKey, opts = {}) => {
    if (!cart) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/workstream/carts/${cart.id}/draft`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intent, ownerKey, varyTone: !!opts.varyTone, forceRegenerate: true }),
      });
      const data = await res.json();
      if (data.success && data.drafts[ownerKey]) {
        setDrafts((d) => ({ ...d, [ownerKey]: data.drafts[ownerKey] }));
      }
    } finally { setBusy(false); }
  }, [cart, intent]);

  const sendAll = useCallback(async (approvals) => {
    if (!cart) return null;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API}/api/workstream/carts/${cart.id}/send`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approvals }),
      });
      // The gateway kills long responses with plain text ("stream timeout"),
      // which is not JSON — surface a truthful message instead of a parse
      // error: by that point the server has usually finished sending.
      const raw = await res.text();
      let data = null;
      try { data = JSON.parse(raw); } catch { /* non-JSON body */ }
      if (!data) {
        throw new Error(res.ok
          ? 'The server returned an unexpected response. Check the Outreach tab to confirm what was sent.'
          : 'The connection timed out while the emails were being dispatched. They most likely still went out — check the Outreach tab before sending again.');
      }
      if (!data.success) throw new Error(data.message || 'send failed');
      return data;
    } catch (e) { setError(e.message); return null; }
    finally { setBusy(false); }
  }, [cart]);

  const startNew = () => {
    setCart(null); setActiveCart(null); setItems([]); setIntent(''); setDigests([]); setDrafts({}); setStep(1);
  };

  return (
    <div>
      <Stepper step={step} setStep={(s) => { if (s < step) setStep(s); }} />
      {error && <ErrorBanner message={error} />}
      {step === 1 && <PickStep cart={cart} items={items} onAdd={addItems} onRemove={removeItem} onContinue={() => setStep(2)} />}
      {step === 2 && <IntentStep cart={cart} items={items} intent={intent} setIntent={setIntent} onBack={() => setStep(1)} onContinue={continueToGroup} busy={busy} />}
      {step === 3 && <DraftStep cartId={cart?.id} digests={digests} drafts={drafts} onRegenerate={regenerateDraft} onSend={sendAll} onStartNew={startNew} busy={busy} />}
    </div>
  );
}

function Stepper({ step, setStep }) {
  const { t } = useT();
  const steps = [{ n: 1, label: 'Pick items' }, { n: 2, label: 'Frame the ask' }, { n: 3, label: 'Approve & send' }];
  return (
    <div className="flex items-center gap-2 mb-6">
      {steps.map((s, i) => {
        const active = s.n === step;
        const done = s.n < step;
        const cls = active
          ? t('bg-blue-50 border-blue-300 text-blue-800', 'bg-blue-500/15 border-blue-500/40 text-blue-200')
          : done
            ? `${t('bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100', 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20')} cursor-pointer`
            : t('bg-slate-100 border-slate-200 text-slate-400', 'bg-slate-800/50 border-slate-700/40 text-slate-500');
        const bubbleCls = active
          ? 'bg-blue-600 text-white'
          : done
            ? 'bg-emerald-500 text-white'
            : t('bg-slate-200 text-slate-500', 'bg-slate-700 text-slate-400');
        return (
          <div key={s.n} className="flex items-center gap-2">
            <button type="button" onClick={() => done && setStep(s.n)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition border ${cls}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${bubbleCls}`}>
                {s.n}
              </span>
              {s.label}
            </button>
            {i < steps.length - 1 && <span className={t("text-slate-300", "text-slate-700")}>|</span>}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Pick items
// ---------------------------------------------------------------------------

function PickStep({ cart, items, onAdd, onRemove, onContinue }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
      <ItemPicker cartItems={items} onAdd={onAdd} />
      <CartPanel cart={cart} items={items} onRemove={onRemove} onContinue={onContinue} />
    </div>
  );
}

// ItemPicker — shows ONE collapsed row per first-class PSI app (Risk, CAP,
// Mandate, etc.). Sub-modules within an app (e.g. cap_milestone for CAP,
// ppm_drift for Policy Management) are rolled up — the user sees the app,
// not the sub-tab. Click an app to expand and pick its items.
function ItemPicker({ cartItems, onAdd }) {
  const { t } = useT();
  const [allItems, setAllItems] = useState([]);
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dueWithin, setDueWithin] = useState(null);
  const [apps, setApps] = useState([]);              // app catalog (top-level)
  const [sourceToApp, setSourceToApp] = useState({}); // source_module → app id
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set()); // app ids that are open

  // Load catalog — APPS, not sub-modules
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/workstream/catalog`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setApps(data.apps || []);
          setSourceToApp(data.source_to_app || {});
        }
      } catch {}
    })();
  }, []);

  // Load items
  const reload = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    if (dueWithin) params.set('due_within_days', String(dueWithin));
    try {
      const res = await fetch(`${API}/api/workstream/items?${params.toString()}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setAllItems(data.items || []);
        setDiagnostics(data.diagnostics || []);
      }
    } finally { setLoading(false); }
  }, [search, dueWithin]);
  useEffect(() => { reload(); }, [reload]);

  const inCart = useMemo(() => new Set(cartItems.map((it) => `${it.source_module}:${it.source_id}`)), [cartItems]);

  // Assigned-person filter: pick one owner and see only their items across
  // every module — the "select all of Maria's risks in one pass" workflow.
  const [ownerFilter, setOwnerFilter] = useState('');
  const ownerKeyOf = (it) => (it.owner_user_id ? `uid:${it.owner_user_id}`
    : it.owner_email ? `email:${String(it.owner_email).toLowerCase()}` : null);
  const owners = useMemo(() => {
    const m = new Map();
    for (const it of allItems) {
      const key = ownerKeyOf(it);
      if (key && !m.has(key)) m.set(key, it.owner_name || it.owner_email || 'Unknown');
    }
    return [...m.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [allItems]);
  const visibleItems = useMemo(
    () => (ownerFilter ? allItems.filter((it) => ownerKeyOf(it) === ownerFilter) : allItems),
    [allItems, ownerFilter]
  );

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const addSelected = async () => {
    const toAdd = allItems.filter((it) => selected.has(`${it.source_module}:${it.source_id}`));
    if (toAdd.length === 0) return;
    await onAdd(toAdd);
    setSelected(new Set());
  };

  // State-contract obligations support a direct owner reminder: expand the
  // Compliance Hub row, select one or more, email the owners now.
  const smacSelectedIds = useMemo(
    () => [...selected].filter((k) => k.startsWith('smac_obligation:')).map((k) => k.slice('smac_obligation:'.length)),
    [selected]
  );
  const [smacReminderNote, setSmacReminderNote] = useState('');
  const [smacSending, setSmacSending] = useState(false);
  const sendSmacReminders = async () => {
    if (!smacSelectedIds.length || smacSending) return;
    setSmacSending(true); setSmacReminderNote('');
    try {
      const d = await fetch(`${API}/api/smac/obligations/remind`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ obligation_ids: smacSelectedIds }),
      }).then((r) => r.json());
      if (d.success) {
        const parts = [`${d.sent} reminder${d.sent === 1 ? '' : 's'} sent to the owner${d.sent === 1 ? '' : 's'}`];
        if (d.no_owner) parts.push(`${d.no_owner} skipped (no owner assigned)`);
        if (d.failed) parts.push(`${d.failed} failed`);
        setSmacReminderNote(parts.join(', ') + '.');
        setSelected((prev) => new Set([...prev].filter((k) => !k.startsWith('smac_obligation:'))));
      } else {
        setSmacReminderNote(d.message || 'The reminders could not be sent.');
      }
    } catch {
      setSmacReminderNote('The reminders could not be sent.');
    }
    setSmacSending(false);
  };

  // Vendor follow-ups (assessments, scorecards, FDR COA) go through the SAME
  // Add to outreach flow as everything else — one path, the full process:
  // pick the items, pick the people from the vendor's recorded contacts,
  // draft, send (operator order 2026-08-27; the one-click reminder is gone).

  // Group items by APP (rolling up sub-modules). Result: Map<appId, items[]>.
  const groupedByApp = useMemo(() => {
    const g = new Map();
    for (const it of visibleItems) {
      const appId = sourceToApp[it.source_module] || it.source_module;
      if (!g.has(appId)) g.set(appId, []);
      g.get(appId).push(it);
    }
    return g;
  }, [visibleItems, sourceToApp]);

  // Items are ONLY shown when their app is expanded. By default, apps are
  // collapsed — the user sees a clean list of apps with item counts.
  const toggleApp = (appId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(appId)) next.delete(appId); else next.add(appId);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title…"
            className={`flex-1 rounded-xl px-4 py-2.5 text-sm border focus:outline-none ${t('bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-500', 'bg-slate-900 border-slate-700/60 text-white placeholder-slate-500 focus:border-blue-500')}`}
          />
          <FilterChip label="Due in 7 days" active={dueWithin === 7} onClick={() => setDueWithin(dueWithin === 7 ? null : 7)} />
          <FilterChip label="Due in 30 days" active={dueWithin === 30} onClick={() => setDueWithin(dueWithin === 30 ? null : 30)} />
          <select
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            title="Show only items assigned to one person"
            className={`rounded-xl px-3 py-2.5 text-sm border focus:outline-none ${t('bg-white border-slate-200 text-slate-700 focus:border-blue-500', 'bg-slate-900 border-slate-700/60 text-slate-200 focus:border-blue-500')}`}
          >
            <option value="">All assignees</option>
            {owners.map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {selected.size > 0 && (
        <div className={`sticky top-0 z-10 flex items-center justify-between gap-3 flex-wrap p-3 rounded-xl border backdrop-blur ${t('bg-blue-50 border-blue-200', 'bg-blue-500/10 border-blue-500/30')}`}>
          <span className={`text-sm ${t('text-blue-800', 'text-blue-100')}`}>
            <strong className={t('text-blue-900', 'text-white')}>{selected.size}</strong> selected
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            {smacSelectedIds.length > 0 && (
              <button type="button" onClick={sendSmacReminders} disabled={smacSending}
                title="Emails each selected state-contract obligation to its owner, with the due date and a link to complete it"
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition disabled:opacity-50 ${t('bg-slate-800 text-white hover:bg-slate-700', 'bg-slate-200 text-slate-900 hover:bg-white')}`}>
                {smacSending ? 'Sending…' : `Email a reminder to the owner${smacSelectedIds.length > 1 ? 's' : ''} (${smacSelectedIds.length})`}
              </button>
            )}
            <button type="button" onClick={addSelected}
              className="px-3 py-1.5 bg-gradient-to-r from-orange-600 to-amber-500 rounded-lg text-sm font-semibold text-white hover:shadow-lg hover:shadow-blue-500/25 transition">
              Add to outreach
            </button>
          </div>
        </div>
      )}
      {smacReminderNote && (
        <p className={`text-xs font-medium ${t('text-emerald-700', 'text-emerald-300')}`}>{smacReminderNote}</p>
      )}

      {loading ? <Loader /> : (
        <div className="space-y-2">
          {apps.map((app) => {
            const list = groupedByApp.get(app.id) || [];
            const isOpen = expanded.has(app.id);
            const inCartCount = list.filter((it) => inCart.has(`${it.source_module}:${it.source_id}`)).length;
            const isEmpty = list.length === 0;
            return (
              <div key={app.id} className={`rounded-xl border overflow-hidden ${isEmpty ? 'opacity-60' : ''} ${t('bg-white border-slate-200', 'bg-slate-900/40 border-slate-700/40')}`}>
                <button
                  type="button"
                  onClick={() => !isEmpty && toggleApp(app.id)}
                  disabled={isEmpty}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition ${isEmpty ? 'cursor-default' : t('hover:bg-slate-50', 'hover:bg-slate-900')}`}
                >
                  <span className={`text-[10px] font-bold flex-shrink-0 w-4 text-center ${isEmpty ? 'opacity-30' : ''} ${t('text-slate-500', 'text-slate-400')}`}>{isOpen ? '−' : '+'}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${t('text-slate-900', 'text-white')}`}>{app.label}</p>
                    {app.description && <p className={`text-[11px] truncate ${t('text-slate-500', 'text-slate-500')}`}>{app.description}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {inCartCount > 0 && (
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${t('bg-emerald-100 text-emerald-700', 'bg-emerald-500/15 text-emerald-300')}`}>
                        {inCartCount} in cart
                      </span>
                    )}
                    {isEmpty ? (
                      <span className={`text-[10px] font-medium uppercase tracking-wide ${t('text-slate-400', 'text-slate-500')}`}>nothing pending</span>
                    ) : (
                      <span className={`text-xs font-semibold tabular-nums ${t('text-slate-600', 'text-slate-400')}`}>{list.length}</span>
                    )}
                  </div>
                </button>
                {isOpen && !isEmpty && (
                  <div className={`border-t px-3 py-2 space-y-1 ${t('border-slate-100 bg-slate-50/50', 'border-slate-800 bg-slate-950/40')}`}>
                    {list.every((it) => it.source_module === 'mandate_action') ? (
                      <MandateMemoGroups list={list} inCart={inCart} selected={selected} toggle={toggle} />
                    ) : list.every((it) => it.source_module === 'coa_activity') ? (
                      <CoaActivityGroups list={list} inCart={inCart} selected={selected} toggle={toggle} />
                    ) : list.every((it) => it.source_module === 'risk') ? (
                      <RiskGroups list={list} inCart={inCart} selected={selected} toggle={toggle} />
                    ) : list.map((it) => {
                      const k = `${it.source_module}:${it.source_id}`;
                      const alreadyAdded = inCart.has(k);
                      return (
                        <ItemRow
                          key={k}
                          item={it}
                          selected={selected.has(k)}
                          disabled={alreadyAdded}
                          onToggle={() => toggle(k)}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <DiagnosticsBar diagnostics={diagnostics} />
        </div>
      )}
    </div>
  );
}

/**
 * Mandate items grouped at the MEMO level (operator rule 2026-08-10): the
 * Build list shows one row per memo — the unit a compliance officer thinks
 * in — and expands on demand to the people being chased and each person's
 * open actions. Selecting the memo row selects every person on it; the
 * expansion allows per-person choices.
 */
function MandateMemoGroups({ list, inCart, selected, toggle }) {
  const { t } = useT();
  const [open, setOpen] = useState(() => new Set());
  const groups = useMemo(() => {
    const m = new Map();
    for (const it of list) {
      const memoNo = it.memo_number || String(it.source_id).split('::')[0];
      if (!m.has(memoNo)) m.set(memoNo, []);
      m.get(memoNo).push(it);
    }
    return [...m.entries()];
  }, [list]);
  const flip = (memoNo) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(memoNo)) next.delete(memoNo); else next.add(memoNo);
    return next;
  });
  return (
    <div className="space-y-1">
      {groups.map(([memoNo, items]) => {
        const keys = items.map((it) => `${it.source_module}:${it.source_id}`);
        const selectable = keys.filter((k) => !inCart.has(k));
        const allSelected = selectable.length > 0 && selectable.every((k) => selected.has(k));
        const someSelected = selectable.some((k) => selected.has(k));
        const allInCart = selectable.length === 0;
        const actionCount = items.reduce((n, it) => n + (it.actions?.length || 0), 0);
        const isOpen = open.has(memoNo);
        const due = items.map((it) => it.due_date).filter(Boolean).sort()[0] || null;
        return (
          <div key={memoNo} className={`rounded-lg border overflow-hidden ${t('bg-white border-slate-200', 'bg-slate-900/60 border-slate-700/40')}`}>
            <div className="flex items-start gap-3 p-3">
              <button type="button" aria-label={allSelected ? 'Deselect memo' : 'Select memo'}
                onClick={allInCart ? undefined : () => selectable.forEach((k) => {
                  if (allSelected ? selected.has(k) : !selected.has(k)) toggle(k);
                })}
                className={`w-4 h-4 rounded-md border-2 mt-0.5 flex-shrink-0 flex items-center justify-center ${
                  allInCart ? t('border-slate-300 bg-slate-100', 'border-slate-700 bg-slate-800')
                  : allSelected ? 'border-blue-600 bg-blue-600'
                  : someSelected ? 'border-blue-600 bg-blue-200'
                  : t('border-slate-300', 'border-slate-600')}`}>
                {(allSelected || someSelected || allInCart) && <span className="w-1.5 h-1.5 rounded-sm bg-white" />}
              </button>
              <button type="button" onClick={() => flip(memoNo)} className="flex-1 min-w-0 text-left">
                <p className={`text-sm font-medium truncate ${t('text-slate-900', 'text-white')}`}>{items[0]?.title || memoNo}</p>
                <div className={`flex items-center gap-2 mt-0.5 text-[11px] ${t('text-slate-500', 'text-slate-500')}`}>
                  <span>{memoNo}</span>
                  <span>· {actionCount || items.length} open action{(actionCount || items.length) === 1 ? '' : 's'}</span>
                  <span>· {items.length} {items.length === 1 ? 'person' : 'people'}</span>
                  {due && <span>· due {fmtDate(due)}</span>}
                  {allInCart && <span className={`px-1.5 py-0.5 rounded font-semibold ${t('bg-emerald-100 text-emerald-700', 'bg-emerald-500/15 text-emerald-300')}`}>in cart</span>}
                </div>
              </button>
              <button type="button" onClick={() => flip(memoNo)}
                className={`text-[10px] font-bold flex-shrink-0 w-4 text-center mt-1 ${t('text-slate-500', 'text-slate-400')}`}>
                {isOpen ? '−' : '+'}
              </button>
            </div>
            {isOpen && (
              <div className={`border-t px-3 py-2 space-y-1.5 ${t('border-slate-100 bg-slate-50/60', 'border-slate-800 bg-slate-950/40')}`}>
                {items.map((it) => {
                  const k = `${it.source_module}:${it.source_id}`;
                  const alreadyAdded = inCart.has(k);
                  return (
                    <div key={k}>
                      <ItemRow item={{ ...it, title: it.owner_name || it.owner_email || 'Unassigned' }}
                        selected={selected.has(k)} disabled={alreadyAdded} onToggle={() => toggle(k)} />
                      {(it.actions || []).length > 0 && (
                        <ul className={`ml-10 mt-1 space-y-0.5 text-[11.5px] ${t('text-slate-600', 'text-slate-400')}`}>
                          {it.actions.map((a, i) => (
                            <li key={i} className="flex items-baseline gap-1.5">
                              <span aria-hidden className={t('text-slate-300', 'text-slate-600')}>•</span>
                              <span className="min-w-0 flex-1">{a.title}</span>
                              {a.due_date && <span className={`whitespace-nowrap ${t('text-slate-400', 'text-slate-500')}`}>due {fmtDate(a.due_date)}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// COA rows arrive one per PERIOD — a monthly measure is twelve rows. The
// picker reads at the ACTIVITY level (operator order 2026-08-19): one
// collapsed row per activity (component · entity · frequency), expand to
// select the month or months the outreach covers.
function CoaActivityGroups({ list, inCart, selected, toggle }) {
  const { t } = useT();
  const [open, setOpen] = useState(() => new Set());
  const groups = useMemo(() => {
    const m = new Map();
    for (const it of list) {
      const gk = [it.component || it.title, it.entity_name || '', it.activity_frequency || ''].join('::');
      if (!m.has(gk)) m.set(gk, []);
      m.get(gk).push(it);
    }
    return [...m.entries()];
  }, [list]);
  const flip = (gk) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(gk)) next.delete(gk); else next.add(gk);
    return next;
  });
  return (
    <div className="space-y-1">
      {groups.map(([gk, items]) => {
        const first = items[0] || {};
        const keys = items.map((it) => `${it.source_module}:${it.source_id}`);
        const selectable = keys.filter((k) => !inCart.has(k));
        const allSelected = selectable.length > 0 && selectable.every((k) => selected.has(k));
        const someSelected = selectable.some((k) => selected.has(k));
        const allInCart = selectable.length === 0;
        const isOpen = open.has(gk);
        const owner = first.owner_name || first.owner_email;
        const heading = [first.component || first.title, first.entity_name].filter(Boolean).join(' — ');
        return (
          <div key={gk} className={`rounded-lg border overflow-hidden ${t('bg-white border-slate-200', 'bg-slate-900/60 border-slate-700/40')}`}>
            <div className="flex items-start gap-3 p-3">
              <button type="button" aria-label={allSelected ? 'Deselect activity' : 'Select activity'}
                onClick={allInCart ? undefined : () => selectable.forEach((k) => {
                  if (allSelected ? selected.has(k) : !selected.has(k)) toggle(k);
                })}
                className={`w-4 h-4 rounded-md border-2 mt-0.5 flex-shrink-0 flex items-center justify-center ${
                  allInCart ? t('border-slate-300 bg-slate-100', 'border-slate-700 bg-slate-800')
                  : allSelected ? 'border-blue-600 bg-blue-600'
                  : someSelected ? 'border-blue-600 bg-blue-200'
                  : t('border-slate-300', 'border-slate-600')}`}>
                {(allSelected || someSelected || allInCart) && <span className="w-1.5 h-1.5 rounded-sm bg-white" />}
              </button>
              <button type="button" onClick={() => flip(gk)} className="flex-1 min-w-0 text-left">
                <p className={`text-sm font-medium truncate ${t('text-slate-900', 'text-white')}`}>{heading}</p>
                <div className={`flex items-center gap-2 mt-0.5 text-[11px] ${t('text-slate-500', 'text-slate-500')}`}>
                  {first.activity_frequency && <span>{first.activity_frequency}</span>}
                  <span>· {items.length} open period{items.length === 1 ? '' : 's'}</span>
                  {owner
                    ? <span className={t('text-slate-600', 'text-slate-400')}>· assigned: {owner}</span>
                    : <span className="text-red-500">· unassigned</span>}
                  {allInCart && <span className={`px-1.5 py-0.5 rounded font-semibold ${t('bg-emerald-100 text-emerald-700', 'bg-emerald-500/15 text-emerald-300')}`}>in cart</span>}
                </div>
              </button>
              <button type="button" onClick={() => flip(gk)}
                className={`text-[10px] font-bold flex-shrink-0 w-4 text-center mt-1 ${t('text-slate-500', 'text-slate-400')}`}>
                {isOpen ? '−' : '+'}
              </button>
            </div>
            {isOpen && (
              <div className={`border-t px-3 py-2 space-y-1 ${t('border-slate-100 bg-slate-50/50', 'border-slate-800 bg-slate-950/40')}`}>
                {items.map((it) => {
                  const k = `${it.source_module}:${it.source_id}`;
                  const alreadyAdded = inCart.has(k);
                  return (
                    <ItemRow
                      key={k}
                      item={{ ...it, title: it.period_label || it.title }}
                      selected={selected.has(k)}
                      disabled={alreadyAdded}
                      onToggle={() => toggle(k)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Risks grouped umbrella-first (operator rule 2026-08-19: follow-ups are
 * conducted FROM Workstream): one row per umbrella (or standalone risk),
 * expanding to its sub-risks. Ticking the group selects every sub-risk;
 * the expansion allows per-child choices — each carted item references the
 * exact risk id, which is what routes the owner's reply to the right child.
 */
function RiskGroups({ list, inCart, selected, toggle }) {
  const { t } = useT();
  const [open, setOpen] = useState(() => new Set());
  const groups = useMemo(() => {
    const m = new Map();
    for (const it of list) {
      const gk = it.parent_risk_id || it.source_id;
      if (!m.has(gk)) m.set(gk, []);
      m.get(gk).push(it);
    }
    // Umbrella row first inside each group, then its children.
    for (const items of m.values()) items.sort((a, b) => (a.parent_risk_id ? 1 : 0) - (b.parent_risk_id ? 1 : 0));
    return [...m.entries()];
  }, [list]);
  const flip = (gk) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(gk)) next.delete(gk); else next.add(gk);
    return next;
  });
  return (
    <div className="space-y-1">
      {groups.map(([gk, items]) => {
        const first = items[0] || {};
        const heading = first.parent_title || first.title;
        const children = items.filter((it) => !!it.parent_risk_id);
        const keys = items.map((it) => `${it.source_module}:${it.source_id}`);
        const selectable = keys.filter((k) => !inCart.has(k));
        const allSelected = selectable.length > 0 && selectable.every((k) => selected.has(k));
        const someSelected = selectable.some((k) => selected.has(k));
        const allInCart = selectable.length === 0;
        const isOpen = open.has(gk);
        const owner = first.owner_name || first.owner_email;
        return (
          <div key={gk} className={`rounded-lg border overflow-hidden ${t('bg-white border-slate-200', 'bg-slate-900/60 border-slate-700/40')}`}>
            <div className="flex items-start gap-3 p-3">
              <button type="button" aria-label={allSelected ? 'Deselect risk' : 'Select risk'}
                onClick={allInCart ? undefined : () => selectable.forEach((k) => {
                  if (allSelected ? selected.has(k) : !selected.has(k)) toggle(k);
                })}
                className={`w-4 h-4 rounded-md border-2 mt-0.5 flex-shrink-0 flex items-center justify-center ${
                  allInCart ? t('border-slate-300 bg-slate-100', 'border-slate-700 bg-slate-800')
                  : allSelected ? 'border-blue-600 bg-blue-600'
                  : someSelected ? 'border-blue-600 bg-blue-200'
                  : t('border-slate-300', 'border-slate-600')}`}>
                {(allSelected || someSelected || allInCart) && <span className="w-1.5 h-1.5 rounded-sm bg-white" />}
              </button>
              <button type="button" onClick={() => flip(gk)} className="flex-1 min-w-0 text-left">
                <p className={`text-sm font-medium truncate ${t('text-slate-900', 'text-white')}`}>{heading}</p>
                <div className={`flex items-center gap-2 mt-0.5 text-[11px] ${t('text-slate-500', 'text-slate-500')}`}>
                  {children.length > 0
                    ? <span>Umbrella · {children.length} sub-risk{children.length === 1 ? '' : 's'}</span>
                    : <span>Risk</span>}
                  {owner
                    ? <span className={t('text-slate-600', 'text-slate-400')}>· owner: {owner}</span>
                    : <span className="text-red-500">· no owner</span>}
                  {allInCart && <span className={`px-1.5 py-0.5 rounded font-semibold ${t('bg-emerald-100 text-emerald-700', 'bg-emerald-500/15 text-emerald-300')}`}>in cart</span>}
                </div>
              </button>
              <button type="button" onClick={() => flip(gk)}
                className={`text-[10px] font-bold flex-shrink-0 w-4 text-center mt-1 ${t('text-slate-500', 'text-slate-400')}`}>
                {isOpen ? '−' : '+'}
              </button>
            </div>
            {isOpen && (
              <div className={`border-t px-3 py-2 space-y-1 ${t('border-slate-100 bg-slate-50/50', 'border-slate-800 bg-slate-950/40')}`}>
                {items.map((it) => {
                  const k = `${it.source_module}:${it.source_id}`;
                  const alreadyAdded = inCart.has(k);
                  return (
                    <ItemRow
                      key={k}
                      item={{ ...it, title: it.parent_risk_id ? it.title : `${it.title}${children.length ? ' (umbrella)' : ''}` }}
                      selected={selected.has(k)}
                      disabled={alreadyAdded}
                      onToggle={() => toggle(k)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ItemRow({ item, selected, disabled, onToggle }) {
  const { t } = useT();
  const baseCls = disabled
    ? `${t('bg-white/40 border-slate-100', 'bg-slate-900/40 border-slate-800')} cursor-default opacity-60`
    : selected
      ? t('bg-blue-50 border-blue-300', 'bg-blue-500/10 border-blue-500/40')
      : `${t('bg-white border-slate-200 hover:border-slate-300', 'bg-slate-900/60 border-slate-700/40 hover:border-slate-600 hover:bg-slate-900')}`;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onToggle}
      className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition ${baseCls}`}
    >
      <div className={`w-4 h-4 rounded-md border-2 mt-0.5 flex-shrink-0 flex items-center justify-center ${
        disabled ? t('border-slate-300 bg-slate-100', 'border-slate-700 bg-slate-800')
                 : selected ? 'border-blue-600 bg-blue-600'
                            : t('border-slate-300', 'border-slate-600')
      }`}>
        {(selected || disabled) && <span className="w-1.5 h-1.5 rounded-sm bg-white" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm truncate ${t('text-slate-900', 'text-white')}`}>{item.title}</p>
        <div className={`flex items-center gap-2 mt-0.5 text-[11px] ${t('text-slate-500', 'text-slate-500')}`}>
          {item.status && <span className={`px-1.5 py-0.5 rounded ${t('bg-slate-100 text-slate-700', 'bg-slate-800 text-slate-300')}`}>{item.status}</span>}
          {item.due_date && <span>due {fmtDate(item.due_date)}</span>}
          {item.priority && <span className="text-amber-500">priority: {item.priority}</span>}
          {(item.owner_name || item.owner_email)
            ? <span className={t('text-slate-600', 'text-slate-400')}>assigned: {item.owner_name || item.owner_email}</span>
            : <span className="text-red-500">unassigned</span>}
        </div>
      </div>
      {disabled && <span className={`text-[10px] font-semibold uppercase tracking-wide ${t('text-emerald-600', 'text-emerald-400')}`}>Added</span>}
    </button>
  );
}

function CartPanel({ cart, items, onRemove, onContinue }) {
  const { t } = useT();
  const ownerCount = useMemo(() => {
    const s = new Set();
    for (const it of items) {
      if (it.owner_user_id) s.add(`uid:${it.owner_user_id}`);
      else if (it.owner_email) s.add(`email:${String(it.owner_email).toLowerCase()}`);
      else s.add('unassigned');
    }
    return s.size;
  }, [items]);

  return (
    <div className={`rounded-2xl border p-5 h-fit lg:sticky lg:top-6 ${t('bg-white border-slate-200', 'bg-slate-900/60 border-slate-700/40')}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-sm font-bold ${t('text-slate-900', 'text-white')}`}>Your outreach</h3>
        <span className={`text-xs ${t('text-slate-500', 'text-slate-400')}`}>{items.length} item{items.length === 1 ? '' : 's'} · {ownerCount} owner{ownerCount === 1 ? '' : 's'}</span>
      </div>
      {items.length === 0 ? (
        <p className={`text-xs leading-relaxed ${t('text-slate-500', 'text-slate-500')}`}>
          Pick items on the left. We'll group them by owner and draft one brief email per person.
        </p>
      ) : (
        <ul className="space-y-1.5 mb-4 max-h-80 overflow-auto pr-1">
          {items.map((it) => (
            <li key={it.id} className={`flex items-start gap-2 p-2 rounded-lg ${t('bg-slate-50', 'bg-slate-800/40')}`}>
              <div className="flex-1 min-w-0">
                <p className={`text-xs truncate ${t('text-slate-800', 'text-white')}`}>{it.title}</p>
                <p className={`text-[10px] ${t('text-slate-500', 'text-slate-500')}`}>{it.source_module.replace(/_/g, ' ')}</p>
              </div>
              <button type="button" onClick={() => onRemove(it.id)} title="Remove"
                className={`text-sm flex-shrink-0 ${t('text-slate-400 hover:text-red-500', 'text-slate-500 hover:text-red-300')}`}>×</button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" onClick={onContinue} disabled={items.length === 0}
        className="w-full px-4 py-2.5 bg-gradient-to-r from-orange-600 to-amber-500 rounded-lg font-semibold text-sm text-white disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-blue-500/25 transition">
        Continue
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Frame the ask
// ---------------------------------------------------------------------------

function IntentStep({ cart, items, intent, setIntent, onBack, onContinue, busy }) {
  const { t } = useT();
  const placeholderRotation = [
    'We need evidence by Friday for the CMS audit kickoff on Monday.',
    'Quick status check — are these still on track this week?',
    'Q3 follow-up: please confirm completion or update target date.',
    'Validation reminder — please review and approve in PSI.',
  ];
  const [placeholder] = useState(() => placeholderRotation[Math.floor(Math.random() * placeholderRotation.length)]);

  const ownerCount = cart?.owner_count || estimateOwnerCount(items);
  return (
    <div className="max-w-4xl mx-auto space-y-5">
      <div>
        <h2 className={`text-lg font-semibold mb-1 ${t('text-slate-900', 'text-white')}`}>What are you asking for?</h2>
        <p className={`text-sm ${t('text-slate-600', 'text-slate-400')}`}>
          Write the message you want them to read. It goes out in your words; PSI only corrects spelling and grammar, adds the greeting from their registered names, and signs it with yours.
        </p>
      </div>

      <div className={`rounded-2xl border shadow-lg overflow-hidden ${t('bg-white border-slate-200', 'bg-slate-900 border-slate-700/60')}`}>
        <div className={`flex items-center justify-between px-5 py-3 border-b ${t('bg-slate-50 border-slate-200', 'bg-slate-800/60 border-slate-700/60')}`}>
          <p className={`text-sm font-semibold ${t('text-slate-800', 'text-white')}`}>New follow-up</p>
          <p className={`text-[11px] ${t('text-slate-500', 'text-slate-400')}`}>{items.length} item{items.length === 1 ? '' : 's'} · {ownerCount} owner{ownerCount === 1 ? '' : 's'}</p>
        </div>
        <div className={`flex items-center gap-3 px-5 py-2.5 border-b text-sm ${t('border-slate-100', 'border-slate-800')}`}>
          <span className={`text-xs font-semibold w-14 ${t('text-slate-400', 'text-slate-500')}`}>To</span>
          <span className={t('text-slate-700', 'text-slate-200')}>
            {ownerCount === 1 ? 'The item’s owner' : `${ownerCount} owners`} — each gets one email listing only their items
          </span>
        </div>
        <div className={`flex items-center gap-3 px-5 py-2.5 border-b text-sm ${t('border-slate-100', 'border-slate-800')}`}>
          <span className={`text-xs font-semibold w-14 ${t('text-slate-400', 'text-slate-500')}`}>Subject</span>
          <span className={`italic ${t('text-slate-400', 'text-slate-500')}`}>Written for you from your message below</span>
        </div>
        <textarea
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          placeholder={placeholder}
          rows={10}
          maxLength={1500}
          className={`w-full px-5 py-4 text-[15px] leading-relaxed border-0 focus:outline-none resize-none min-h-[16rem] ${t('bg-white text-slate-900 placeholder-slate-400', 'bg-slate-900 text-white placeholder-slate-500')}`}
        />
        <div className={`flex items-center justify-between px-5 py-3 border-t ${t('bg-slate-50 border-slate-200', 'bg-slate-800/40 border-slate-700/60')}`}>
          <p className={`text-xs ${t('text-slate-500', 'text-slate-400')}`}>
            Your wording is kept. Typos and grammar are corrected, nothing is rewritten, and you see the exact email before it sends.
          </p>
          <p className={`text-[11px] whitespace-nowrap pl-4 ${t('text-slate-400', 'text-slate-500')}`}>{intent.length}/1500</p>
        </div>
      </div>

      <div className="flex justify-between gap-3">
        <button type="button" onClick={onBack}
          className={`px-4 py-2 text-sm transition ${t('text-slate-500 hover:text-slate-800', 'text-slate-400 hover:text-white')}`}>
          Back
        </button>
        <button type="button" onClick={onContinue} disabled={busy || items.length === 0}
          className="px-5 py-2.5 bg-gradient-to-r from-orange-600 to-amber-500 rounded-lg font-semibold text-sm text-white disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-blue-500/25 transition">
          {busy ? 'Drafting…' : 'Continue to drafts'}
        </button>
      </div>
    </div>
  );
}

function estimateOwnerCount(items) {
  const s = new Set();
  for (const it of items) {
    if (it.owner_user_id) s.add(`uid:${it.owner_user_id}`);
    else if (it.owner_email) s.add(`email:${String(it.owner_email).toLowerCase()}`);
  }
  return s.size;
}

// ---------------------------------------------------------------------------
// Step 3: Drafts
// ---------------------------------------------------------------------------

function DraftStep({ cartId, digests, drafts, onRegenerate, onSend, onStartNew, busy }) {
  const { t } = useT();
  const [approvals, setApprovals] = useState({});
  const [results, setResults] = useState(null);
  // Registered org users — the only valid recipients. Fetched once.
  const [orgUsers, setOrgUsers] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/workstream/users`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => d.success && setOrgUsers(d.users || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setApprovals((prev) => {
      const next = { ...prev };
      for (const [key, draft] of Object.entries(drafts || {})) {
        // An approval may already exist before its draft does: unassigned
        // items (COA months, for one) get their recipients named first and
        // are drafted after. The draft fills in around what is there, so the
        // recipients stay and the email is never sent without a subject.
        if (draft && !draft.error && (!next[key] || !next[key].subject)) {
          next[key] = {
            ...(next[key] || {}),
            subject: draft.subject,
            body_text: draft.body_text,
            body_html: draft.body_html,
            voice_profile_hash: draft.voice_profile_hash,
            ai_model: draft.model,
            brevity_score: draft.brevity_score,
            original_chars: (draft.body_text || '').length,
            skip: next[key]?.skip || false,
          };
        }
      }
      return next;
    });
  }, [drafts]);

  const updateApproval = (key, patch) => setApprovals((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const sendApproved = async () => {
    const list = digests
      .filter((d) => approvals[d.key] && !approvals[d.key].skip
        && (!d.unassigned || (approvals[d.key].recipients || []).length > 0))
      .map((d) => {
        const a = approvals[d.key];
        return {
          owner_key: d.key, subject: a.subject, body_text: a.body_text, body_html: a.body_html,
          voice_profile_hash: a.voice_profile_hash, ai_model: a.ai_model, brevity_score: a.brevity_score,
          user_edited_chars: Math.abs((a.body_text || '').length - (a.original_chars || 0)),
          ...(a.recipients && a.recipients.length ? { recipients: a.recipients.map((r) => ({ kind: r.kind || null, user_id: r.kind === 'vendor' ? null : r.id, email: r.email })) } : {}),
        };
      });
    const result = await onSend(list);
    if (result) setResults(result);
  };

  if (results) return <DraftResults results={results} digests={digests} onStartNew={onStartNew} />;

  const approvedCount = digests.filter((d) => approvals[d.key] && !approvals[d.key].skip
    && (!d.unassigned || (approvals[d.key].recipients || []).length > 0)).length;
  const unassignedCount = digests.filter((d) => d.unassigned).length;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <p className={`text-sm ${t('text-slate-700', 'text-slate-300')}`}>
          <strong className={t('text-slate-900', 'text-white')}>{approvedCount}</strong> of <strong className={t('text-slate-900', 'text-white')}>{digests.filter((d) => !d.unassigned).length}</strong> emails ready to send
        </p>
        {unassignedCount > 0 && (
          <span className={`text-xs px-2 py-1 rounded-full border ${t('bg-amber-50 border-amber-200 text-amber-700', 'bg-amber-500/15 border-amber-500/30 text-amber-300')}`}>
            {unassignedCount} item{unassignedCount === 1 ? '' : 's'} unassigned · won't be sent
          </span>
        )}
      </div>

      <div className="space-y-4">
        {digests.map((digest) => (
          <DigestCard key={digest.key} cartId={cartId} digest={digest} draft={drafts[digest.key]}
            approval={approvals[digest.key]} onChange={(patch) => updateApproval(digest.key, patch)}
            onRegenerate={(opts) => onRegenerate(digest.key, opts)} busy={busy} users={orgUsers} />
        ))}
      </div>

      <div className="sticky bottom-4 mt-8 flex justify-end">
        <div className={`backdrop-blur rounded-2xl px-4 py-3 shadow-2xl flex items-center gap-3 border ${t('bg-white/95 border-slate-200', 'bg-slate-900/95 border-slate-700')}`}>
          <p className={`text-sm ${t('text-slate-700', 'text-slate-300')}`}>
            Ready to send <strong className={t('text-slate-900', 'text-white')}>{approvedCount}</strong> email{approvedCount === 1 ? '' : 's'}
          </p>
          <button type="button" onClick={sendApproved} disabled={approvedCount === 0 || busy}
            className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-blue-600 rounded-lg font-semibold text-sm text-white disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-emerald-500/25 transition">
            {busy ? 'Sending…' : 'Send all approved'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RecipientPicker({ digest, approval, onChange, users, t }) {
  // WHO receives this digest. The pre-assigned owner is the default; any
  // registered user can be chosen instead — or several, each getting their
  // own copy and link. Free-text addresses are impossible by construction.
  const chosen = approval?.recipients || [];
  // A memo digest defaults to EVERYONE who holds part of the memo — one
  // email, all of them on the To line. Removing or adding people here still
  // works exactly as before.
  const defaults = [
    ...(digest.owner_email ? [{ id: digest.owner_user_id, email: digest.owner_email, name: digest.owner_name }] : []),
    ...((digest.co_recipients || []).map((r) => ({ id: r.owner_user_id, email: r.owner_email, name: r.owner_name }))),
  ];
  const shown = chosen.length ? chosen : defaults;
  const add = (id) => {
    const u = users.find((x) => String(x.id) === String(id));
    if (!u) return;
    const base = chosen.length ? chosen : shown;
    if (base.some((r) => String(r.id) === String(u.id))) return;
    onChange({ recipients: [...base, u] });
  };
  const remove = (id) => {
    const base = chosen.length ? chosen : shown;
    const next = base.filter((r) => String(r.id) !== String(id));
    onChange({ recipients: next });
  };
  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[11px] uppercase tracking-wide font-semibold ${t('text-slate-400', 'text-slate-500')}`}>To</span>
        {shown.map((r) => (
          <span key={r.id || r.email} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
            r.kind === 'internal' ? t('bg-sky-50 text-sky-800 ring-1 ring-sky-200', 'bg-sky-500/10 text-sky-200 ring-1 ring-sky-500/30') : t('bg-slate-100 text-slate-700', 'bg-slate-800 text-slate-200')}`}>
            {r.kind === 'internal' && <span className="text-[9.5px] font-bold uppercase tracking-wide opacity-70">Cc</span>}
            {r.name || r.email}
            {(chosen.length > 0 || shown.length > 1) && (
              <button type="button" onClick={() => remove(r.id)} className="opacity-60 hover:opacity-100">✕</button>
            )}
          </span>
        ))}
        <select value="" onChange={(e) => e.target.value && add(e.target.value)}
          className={`rounded-lg text-xs py-1 pr-7 ${t('border-slate-200 bg-white text-slate-600', 'border-slate-700 bg-slate-900 text-slate-300')}`}>
          <option value="">{shown.length ? 'Add another…' : 'Choose recipient…'}</option>
          {users.some((u) => u.kind === 'vendor') ? (
            <>
              <optgroup label="Vendor · receives the email">
                {users.filter((u) => u.kind === 'vendor' && !shown.some((r) => String(r.id) === String(u.id)))
                  .map((u) => <option key={u.id} value={u.id}>{u.name || u.email}{u.title ? ` · ${u.title}` : ''}</option>)}
              </optgroup>
              <optgroup label="Your team · copied">
                {users.filter((u) => u.kind === 'internal' && !shown.some((r) => String(r.id) === String(u.id)))
                  .map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </optgroup>
            </>
          ) : users.filter((u) => !shown.some((r) => String(r.id) === String(u.id)))
            .map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
      </div>
    </div>
  );
}

function DigestCard({ cartId, digest, draft, approval, onChange, onRegenerate, busy, users = [] }) {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);
  // Unassigned items (COA months, for one) are drafted the moment recipients
  // are named, once; the button below stays for a manual retry. Live-caught
  // 2026-09-09: the draft was generated but this card never showed it, so the
  // email could not be read or edited before it went out.
  const named = !!digest.unassigned && (approval?.recipients || []).length > 0;
  const autoDrafted = useRef(false);
  useEffect(() => {
    if (digest.unassigned && named && !draft && !busy && !autoDrafted.current) {
      autoDrafted.current = true;
      onRegenerate();
    }
  }, [digest.unassigned, named, draft, busy, onRegenerate]);

  if (digest.unassigned) {
    return (
      <div className={`rounded-2xl border p-5 ${named ? t('bg-white border-slate-200', 'bg-slate-900/40 border-slate-700/40') : t('bg-amber-50 border-amber-200', 'bg-amber-500/5 border-amber-500/30')}`}>
        <h3 className={`text-sm font-semibold mb-1 ${named ? t('text-slate-900', 'text-white') : t('text-amber-800', 'text-amber-100')}`}>
          {named ? `Will send to ${(approval.recipients || []).map((r) => r.name || r.email).join(', ')}` : `Unassigned items (${digest.items.length})`}
        </h3>
        {!named && (
          <p className={`text-xs mb-2 ${t('text-amber-700', 'text-amber-200/80')}`}>
            These items have no owner. Choose who should receive them and they send with everything else.
          </p>
        )}
        <RecipientPicker digest={digest} approval={approval} onChange={onChange} users={users} t={t} />
        <ul className={`space-y-1 text-xs mt-3 ${named ? t('text-slate-600', 'text-slate-300') : t('text-amber-700', 'text-amber-100/80')}`}>
          {digest.items.slice(0, 5).map((it, i) => <li key={i}>• {it.title}</li>)}
          {digest.items.length > 5 && <li className="opacity-70">…and {digest.items.length - 5} more</li>}
        </ul>
        {named && !draft && (
          busy
            ? <div className={`mt-3 flex items-center gap-2 text-xs ${t('text-slate-500', 'text-slate-400')}`}><Spinner /> Drafting the email…</div>
            : <button type="button" onClick={() => onRegenerate()} disabled={busy}
                className="mt-3 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white disabled:opacity-50">
                Draft the email
              </button>
        )}
        {named && draft && draft.error && (
          <div className={`mt-3 rounded-lg border px-3 py-2 ${t('bg-red-50 border-red-200', 'bg-red-500/5 border-red-500/30')}`}>
            <p className={`text-xs ${t('text-red-800', 'text-red-200')}`}>Couldn't draft this email: {draft.error}</p>
            <button type="button" onClick={() => onRegenerate({})}
              className={`mt-1.5 px-3 py-1 border rounded-lg text-xs ${t('bg-red-100 border-red-200 text-red-700 hover:bg-red-200', 'bg-red-500/20 border-red-500/40 text-red-200 hover:bg-red-500/30')}`}>
              Try again
            </button>
          </div>
        )}
        {named && draft && !draft.error && (
          <div className="mt-4">
            <p className={`text-[11px] uppercase tracking-wide font-semibold mb-1.5 ${t('text-slate-400', 'text-slate-500')}`}>The email · read it, edit it, then send</p>
            <DraftEditor cartId={cartId} digest={digest} draft={draft} approval={approval} onChange={onChange} onRegenerate={onRegenerate}
              busy={busy} expanded={expanded} setExpanded={setExpanded} t={t} />
          </div>
        )}
      </div>
    );
  }

  if (!draft) return (
    <div className={`rounded-2xl border p-5 ${t('bg-white border-slate-200', 'bg-slate-900/40 border-slate-700/40')}`}>
      <div className="flex items-center gap-3 mb-3">
        <Avatar name={digest.owner_name || digest.owner_email} />
        <div>
          <p className={`text-sm font-semibold ${t('text-slate-900', 'text-white')}`}>{digest.owner_name || digest.owner_email}</p>
          <p className={`text-xs ${t('text-slate-500', 'text-slate-500')}`}>{digest.owner_email}</p>
        </div>
      </div>
      <div className={`flex items-center gap-2 text-xs ${t('text-slate-500', 'text-slate-500')}`}><Spinner /> Drafting…</div>
    </div>
  );

  if (draft.error) return (
    <div className={`rounded-2xl border p-5 ${t('bg-red-50 border-red-200', 'bg-red-500/5 border-red-500/30')}`}>
      <p className={`text-sm ${t('text-red-800', 'text-red-200')}`}>Couldn't draft for {digest.owner_email}: {draft.error}</p>
      <button type="button" onClick={() => onRegenerate({})}
        className={`mt-2 px-3 py-1 border rounded-lg text-xs ${t('bg-red-100 border-red-200 text-red-700 hover:bg-red-200', 'bg-red-500/20 border-red-500/40 text-red-200 hover:bg-red-500/30')}`}>
        Try again
      </button>
    </div>
  );

  const skip = !!approval?.skip;
  const sc = approval?.brevity_score ?? draft.brevity_score;

  return (
    <div className={`rounded-2xl border transition ${skip
      ? t('border-slate-200 bg-white/50 opacity-60', 'border-slate-800 bg-slate-900/30 opacity-60')
      : t('border-slate-200 bg-white', 'border-slate-700/40 bg-slate-900/60')}`}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Avatar name={digest.external ? digest.entity_name : (digest.owner_name || digest.owner_email)} />
            <div className="min-w-0">
              <p className={`text-sm font-semibold truncate ${t('text-slate-900', 'text-white')}`}>
                {digest.external ? `${digest.entity_name} · vendor follow-up${digest.cycle_label ? ` (${digest.cycle_label} audit open)` : ''}` : (digest.owner_name || digest.owner_email)}
              </p>
              <p className={`text-xs truncate ${t('text-slate-500', 'text-slate-500')}`}>
                {digest.external ? 'Vendor follow-up — replies land on the audit record' : digest.owner_email}
                {!digest.external && !digest.verified && <span className={`ml-2 ${t('text-amber-600', 'text-amber-300')}`}>(unverified)</span>}
              </p>
              {digest.action_title && (
                <p className={`text-xs truncate mt-0.5 ${t('text-slate-600', 'text-slate-300')}`} title={digest.action_title}>
                  Action: {digest.action_title}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <BrevityBadge score={sc} />
            <label className={`flex items-center gap-1.5 text-xs cursor-pointer select-none ${t('text-slate-500', 'text-slate-400')}`}>
              <input type="checkbox" checked={skip} onChange={(e) => onChange({ skip: e.target.checked })}
                className={`rounded ${t('border-slate-300 bg-white', 'border-slate-600 bg-slate-800 text-slate-500')}`} />
              Skip
            </label>
          </div>
        </div>

        <RecipientPicker digest={digest} approval={approval} onChange={onChange}
          users={digest.external ? [...(digest.recipient_options || []), ...(digest.internal_options || [])] : users} t={t} />
        {digest.external && (
          <p className={`mb-2 mt-2 text-[11.5px] px-3 py-2 rounded-lg border ${t('bg-violet-50/60 border-violet-100 text-slate-600', 'bg-violet-500/10 border-violet-500/20 text-slate-300')}`}>
            One email to this vendor covering everything selected — audit, scorecard, COA. Choose anyone from the
            vendor (contacts, officers, portal participants) and anyone from your own team. Vendor people receive the
            email; your own people are copied, together with you and your compliance officers.{digest.engagement_id
              ? ' The engagement contact gets a fresh secure portal link, the email itself is saved to the audit’s correspondence as sent, and the vendor’s reply — with attachments — files there automatically.'
              : ' No audit is currently open for this vendor, so replies go to you directly.'}
          </p>
        )}

        {digest.memo_number && (
          <p className={`mb-2 text-[11.5px] px-3 py-2 rounded-lg border ${t('bg-blue-50/60 border-blue-100 text-slate-600', 'bg-blue-500/10 border-blue-500/20 text-slate-300')}`}>
            {digest.action_title
              ? <>One email for this action of memo <strong>{digest.memo_number}</strong>: everyone who holds the action shares the To line, and nobody from another action is added. The memo document and the action are added to the email automatically; your note below is the personal part on top. The email is filed under the action's Correspondence, and replies file there too.</>
              : <>One email for memo <strong>{digest.memo_number}</strong>: everyone above shares the To line. The memo document is added to the email automatically; your note below is the personal part on top. The email is filed under the memo's Correspondence, and replies file there too.</>}
          </p>
        )}

        <DraftEditor cartId={cartId} digest={digest} draft={draft} approval={approval} onChange={onChange} onRegenerate={onRegenerate}
          busy={busy} expanded={expanded} setExpanded={setExpanded} t={t} />
      </div>
    </div>
  );
}

// The ONE draft editor: subject and body as they will go out (the send wraps
// them in the client's letterhead and brand color), regenerate, and the list
// of items the email covers. Owned digests and unassigned ones share it.
function DraftEditor({ cartId, digest, draft, approval, onChange, onRegenerate, busy, expanded, setExpanded, t }) {
  return (
    <>
      <input
        type="text"
        value={approval?.subject ?? draft.subject}
        onChange={(e) => onChange({ subject: e.target.value })}
        placeholder="Subject"
        maxLength={70}
        className={`w-full rounded-lg px-3 py-2 text-sm border mb-2 focus:outline-none ${t('bg-white border-slate-200 text-slate-900 focus:border-blue-500', 'bg-slate-800/60 border-slate-700/40 text-white focus:border-blue-500')}`}
      />
      <textarea
        value={approval?.body_text ?? draft.body_text}
        onChange={(e) => onChange({ body_text: e.target.value, body_html: textToBasicHtml(e.target.value) })}
        rows={Math.min(8, Math.max(4, (approval?.body_text || draft.body_text || '').split('\n').length + 1))}
        className={`w-full rounded-lg px-3 py-2 text-sm border focus:outline-none font-sans leading-relaxed ${t('bg-white border-slate-200 text-slate-800 focus:border-blue-500', 'bg-slate-800/60 border-slate-700/40 text-slate-200 focus:border-blue-500')}`}
      />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setExpanded((v) => !v)}
          className={`text-[11px] ${t('text-slate-500 hover:text-slate-800', 'text-slate-400 hover:text-slate-200')}`}>
          {expanded ? 'Hide' : 'Show'} {digest.items.length} item{digest.items.length === 1 ? '' : 's'} this email covers
        </button>
        <span className="flex-1" />
        <button type="button" onClick={() => onRegenerate({})} disabled={busy}
          className={`text-[11px] disabled:opacity-40 ${t('text-slate-500 hover:text-slate-800', 'text-slate-400 hover:text-white')}`}>
          Regenerate
        </button>
        <button type="button" onClick={() => onRegenerate({ varyTone: true })} disabled={busy}
          className={`text-[11px] disabled:opacity-40 ${t('text-slate-500 hover:text-slate-800', 'text-slate-400 hover:text-white')}`}>
          Try a different angle
        </button>
      </div>

      <EmailPreview cartId={cartId} ownerKey={digest.key}
        subject={approval?.subject ?? draft.subject}
        bodyText={approval?.body_text ?? draft.body_text}
        bodyHtml={approval?.body_html ?? draft.body_html} t={t} />

      {expanded && (
        <ul className={`mt-2 space-y-1 text-xs pl-4 border-l-2 ${t('text-slate-600 border-slate-200', 'text-slate-400 border-slate-800')}`}>
          {digest.items.map((it, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className={t('text-slate-400', 'text-slate-600')}>·</span>
              <span className="truncate">{it.title}</span>
              {it.due_date && <span className={t('text-slate-400', 'text-slate-600')}>· due {fmtDate(it.due_date)}</span>}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// The email exactly as the owner will receive it: letterhead, the note
// above, and the record below it (memo with its actions and latest activity,
// risk with its prior updates, issue with what is on file). Built by the
// server with the same code the send uses, so what is approved is what goes
// out. Refreshes as the note is edited.
function EmailPreview({ cartId, ownerKey, subject, bodyText, bodyHtml, t }) {
  const [html, setHtml] = useState('');
  const [state, setState] = useState('loading');
  const [open, setOpen] = useState(true);
  const [height, setHeight] = useState(560);
  useEffect(() => {
    if (!cartId || !ownerKey) return undefined;
    let stop = false;
    setState('loading');
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/workstream/carts/${cartId}/preview`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ owner_key: ownerKey, subject, body_text: bodyText, body_html: bodyHtml }),
        });
        const data = await res.json();
        if (stop) return;
        if (data.success) { setHtml(data.html); setState('ready'); } else setState('error');
      } catch { if (!stop) setState('error'); }
    }, 500);
    return () => { stop = true; clearTimeout(timer); };
  }, [cartId, ownerKey, subject, bodyText, bodyHtml]);
  const onLoad = (e) => {
    try {
      const h = e.target.contentDocument?.body?.scrollHeight;
      if (h) setHeight(Math.min(Math.max(h + 24, 320), 1600));
    } catch { /* keep the default height */ }
  };
  return (
    <div className={`mt-4 rounded-xl border overflow-hidden ${t('border-slate-200 bg-slate-50', 'border-slate-700/40 bg-slate-950/40')}`}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between px-4 py-2.5 text-left ${t('text-slate-700', 'text-slate-300')}`}>
        <span className="text-xs font-semibold">The email as the owner will receive it{state === 'loading' ? ' · updating…' : ''}</span>
        <span className="text-[11px]">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (state === 'error'
        ? <p className={`px-4 pb-3 text-xs ${t('text-amber-700', 'text-amber-300')}`}>The preview could not be built. Try again in a moment.</p>
        : html
          ? <iframe title="Email preview" sandbox="allow-same-origin" srcDoc={html} onLoad={onLoad} style={{ height }} className="w-full border-0 bg-[#F2F5F8]" />
          : <p className={`px-4 pb-3 text-xs ${t('text-slate-500', 'text-slate-500')}`}>Building the preview…</p>)}
    </div>
  );
}

function DraftResults({ results, digests, onStartNew }) {
  const { t } = useT();
  const queued = results.results.filter((r) => r.ok);
  const failed = results.results.filter((r) => !r.ok);
  // The server responds the moment the emails are QUEUED (status
  // 'dispatching') and sends them in the background — the response can no
  // longer time out while an email channel is slow. Poll until every
  // outreach reaches a terminal status so the user sees the truth, not a
  // guess.
  const [statuses, setStatuses] = useState({});
  const idsKey = queued.map((r) => r.outreach_id).filter(Boolean).join(',');
  useEffect(() => {
    if (!idsKey) return undefined;
    let stop = false;
    let timer = null;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/workstream/outreach/status?ids=${idsKey}`, { credentials: 'include' });
        const data = await res.json();
        if (stop) return;
        if (data.success) {
          const next = {};
          for (const o of data.outreach || []) next[o.id] = o.status;
          setStatuses(next);
          if ((data.outreach || []).length > 0 && data.outreach.every((o) => o.status !== 'dispatching')) return;
        }
      } catch { /* transient — keep polling */ }
      if (!stop) timer = setTimeout(poll, 2500);
    };
    timer = setTimeout(poll, 1200);
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [idsKey]);

  const statusOf = (r) => statuses[r.outreach_id] || r.status || 'dispatching';
  const stillSending = queued.some((r) => statusOf(r) === 'dispatching');
  const failedLate = queued.filter((r) => statusOf(r) === 'delivery_failed').length;
  return (
    <div className="max-w-xl mx-auto text-center py-8">
      <h2 className={`text-xl font-bold mb-1 ${t('text-slate-900', 'text-white')}`}>{stillSending ? 'Sending…' : 'Sent'}</h2>
      <p className={`text-sm mb-6 ${t('text-slate-600', 'text-slate-400')}`}>
        {stillSending
          ? `${queued.length} email${queued.length === 1 ? '' : 's'} on the way — you can leave this page, delivery continues.`
          : `${queued.length - failedLate} email${queued.length - failedLate === 1 ? '' : 's'} sent${(failed.length + failedLate) > 0 ? `, ${failed.length + failedLate} failed` : ''}.`}
      </p>
      <div className="space-y-2 text-left mb-8">
        {queued.map((r) => {
          const digest = digests.find((d) => d.key === r.owner_key);
          const st = statusOf(r);
          const pill = st === 'dispatching'
            ? <span className={`text-[10px] font-semibold uppercase tracking-wide animate-pulse ${t('text-amber-600', 'text-amber-400')}`}>Sending…</span>
            : st === 'delivery_failed'
              ? <span className={`text-[10px] font-semibold uppercase tracking-wide ${t('text-red-600', 'text-red-400')}`}>Failed</span>
              : <span className={`text-[10px] font-semibold uppercase tracking-wide ${t('text-emerald-600', 'text-emerald-400')}`}>Sent</span>;
          return (
            <div key={r.owner_key} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${t('bg-white border-slate-200 text-slate-700', 'bg-slate-900/60 border-slate-800 text-slate-300')}`}>
              {pill}
              <span>{digest?.owner_email || r.owner_key}</span>
            </div>
          );
        })}
        {failed.map((r) => {
          const digest = digests.find((d) => d.key === r.owner_key);
          return (
            <div key={r.owner_key} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${t('bg-red-50 border-red-200 text-red-700', 'bg-red-500/5 border-red-500/30 text-red-300')}`}>
              <span className="text-[10px] font-semibold uppercase tracking-wide">Failed</span>
              <span>{digest?.owner_email || r.owner_key}</span>
              <span className={`text-xs ${t('text-red-500', 'text-red-400/70')}`}>— {r.reason || r.error}</span>
            </div>
          );
        })}
      </div>
      <button type="button" onClick={onStartNew}
        className="px-5 py-2.5 bg-gradient-to-r from-orange-600 to-amber-500 rounded-lg font-semibold text-sm text-white hover:shadow-lg hover:shadow-blue-500/25 transition">
        Start a new follow-up
      </button>
    </div>
  );
}

// ===========================================================================
// INBOX
// ===========================================================================

function InboxView() {
  const { t } = useT();
  const [outreach, setOutreach] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renudging, setRenudging] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/workstream/outreach?limit=50`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setOutreach(data.outreach || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const renudge = async (id) => {
    setRenudging(id);
    try {
      const res = await fetch(`${API}/api/workstream/outreach/${id}/renudge`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.success) await load();
    } finally { setRenudging(null); }
  };

  if (loading) return <Loader />;
  if (outreach.length === 0) return <Empty title="No follow-ups sent yet" subtitle="Build your first one in the Build tab." />;

  return (
    <div className={`rounded-2xl border overflow-hidden ${t('bg-white border-slate-200', 'border-slate-800')}`}>
      <table className="w-full text-sm">
        <thead className={`text-[11px] uppercase tracking-wider ${t('bg-slate-50 text-slate-500', 'bg-slate-900/80 text-slate-400')}`}>
          <tr>
            <th className="text-left px-4 py-2.5 font-medium">Recipient</th>
            <th className="text-left px-4 py-2.5 font-medium">Subject</th>
            <th className="text-left px-4 py-2.5 font-medium">Items</th>
            <th className="text-left px-4 py-2.5 font-medium">Sent</th>
            <th className="text-left px-4 py-2.5 font-medium">Status</th>
            <th className="text-right px-4 py-2.5 font-medium"></th>
          </tr>
        </thead>
        <tbody className={`divide-y ${t('divide-slate-100', 'divide-slate-800')}`}>
          {outreach.map((o) => (
            <tr key={o.id} className={t('hover:bg-slate-50', 'hover:bg-slate-900/40')}>
              <td className="px-4 py-3">
                <p className={`text-sm ${t('text-slate-900', 'text-white')}`}>{o.recipient_name || o.recipient_email}</p>
                <p className={`text-[11px] ${t('text-slate-500', 'text-slate-500')}`}>{o.recipient_email}</p>
              </td>
              <td className={`px-4 py-3 truncate max-w-[280px] ${t('text-slate-700', 'text-slate-300')}`}>{o.subject}</td>
              <td className={`px-4 py-3 text-xs ${t('text-slate-500', 'text-slate-400')}`}>{o.item_count}</td>
              <td className={`px-4 py-3 text-xs ${t('text-slate-500', 'text-slate-400')}`}>{fmtRelative(o.sent_at)}</td>
              <td className="px-4 py-3"><OutreachStatusPill o={o} /></td>
              <td className="px-4 py-3 text-right">
                {/* Closed work is not chased. The server reports can_renudge
                    false once the memo behind this outreach has been
                    completed — offering the button and then refusing the click
                    is just a dead end. Compared against false explicitly, so a
                    response without the field behaves exactly as before. */}
                {o.can_renudge === false ? (
                  <span
                    className={`text-xs ${t('text-slate-400', 'text-slate-500')}`}
                    title="The memo behind this follow-up has been closed — there is nothing left to chase."
                  >
                    Closed
                  </span>
                ) : (
                  <button type="button" onClick={() => renudge(o.id)} disabled={renudging === o.id || o.status === 'replied'}
                    className={`text-xs px-2.5 py-1 border rounded-md disabled:opacity-40 disabled:cursor-not-allowed transition ${t('bg-white hover:bg-slate-100 border-slate-200 text-slate-700', 'bg-slate-800 hover:bg-slate-700 border-slate-700 text-slate-200')}`}>
                    {renudging === o.id ? 'Sending…' : 'Re-nudge'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OutreachStatusPill({ o }) {
  const { t } = useT();
  let label = o.status;
  let cls = t('bg-slate-100 text-slate-600 border-slate-200', 'bg-slate-700/40 text-slate-300 border-slate-600/50');
  if (o.status === 'dispatching') { label = 'Sending…'; cls = t('bg-amber-100 text-amber-700 border-amber-200 animate-pulse', 'bg-amber-500/15 text-amber-200 border-amber-500/30 animate-pulse'); }
  if (o.status === 'sent')     { label = 'Sent';     cls = t('bg-slate-100 text-slate-600 border-slate-200', 'bg-slate-700/40 text-slate-300 border-slate-600/50'); }
  if (o.status === 'clicked')  { label = 'Clicked';  cls = t('bg-sky-100 text-sky-700 border-sky-200', 'bg-sky-500/15 text-sky-300 border-sky-500/30'); }
  if (o.status === 'replied')  { label = 'Replied';  cls = t('bg-emerald-100 text-emerald-700 border-emerald-200', 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'); }
  if (o.status === 'renudged') { label = 'Re-nudged'; cls = t('bg-amber-100 text-amber-700 border-amber-200', 'bg-amber-500/15 text-amber-200 border-amber-500/30'); }
  if (o.status === 'delivery_failed') { label = 'Failed'; cls = t('bg-red-100 text-red-700 border-red-200', 'bg-red-500/15 text-red-300 border-red-500/40'); }
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}>{label}</span>;
}

// ===========================================================================
// AUDIT
// ===========================================================================

function AuditView() {
  const { t } = useT();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/workstream/events?limit=200`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setEvents(data.events || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader />;

  return (
    <div>
      <p className={`text-xs mb-3 ${t('text-slate-500', 'text-slate-500')}`}>SOC 2 CC7.2 · append-only · last 200 events</p>
      {events.length === 0 ? <Empty title="No events yet" subtitle="Build a follow-up to see entries appear here." /> : (
        <div className={`rounded-2xl border overflow-hidden ${t('bg-white border-slate-200', 'border-slate-800')}`}>
          <table className="w-full text-sm">
            <thead className={`text-[11px] uppercase tracking-wider ${t('bg-slate-50 text-slate-500', 'bg-slate-900/80 text-slate-400')}`}>
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">When</th>
                <th className="text-left px-4 py-2.5 font-medium">Event</th>
                <th className="text-left px-4 py-2.5 font-medium">Actor</th>
                <th className="text-left px-4 py-2.5 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${t('divide-slate-100', 'divide-slate-800')}`}>
              {events.map((e) => (
                <tr key={e.id}>
                  <td className={`px-4 py-2 text-xs font-mono whitespace-nowrap ${t('text-slate-500', 'text-slate-500')}`}>{new Date(e.created_at).toLocaleString()}</td>
                  <td className={`px-4 py-2 ${t('text-slate-800', 'text-slate-200')}`}>
                    {prettyEvent(e.event_type)}
                    {e.outcome === 'failure' && <span className="ml-2 text-red-500 text-xs">failed</span>}
                  </td>
                  <td className={`px-4 py-2 text-xs ${t('text-slate-600', 'text-slate-400')}`}>{e.actor_email || <span className={t('italic text-slate-400', 'italic text-slate-600')}>system</span>}</td>
                  <td className={`px-4 py-2 text-xs truncate max-w-[280px] ${t('text-slate-500', 'text-slate-500')}`}>{eventDetailSummary(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function prettyEvent(x) { return String(x || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()); }
function eventDetailSummary(e) {
  const m = e.metadata || {};
  if (e.event_type === 'item_added')        return `added ${m.added ?? '?'} item${m.added === 1 ? '' : 's'}`;
  if (e.event_type === 'draft_generated')   return `${m.sentence_count ?? '?'} sentences · brevity ${m.brevity_score ?? '?'}`;
  if (e.event_type === 'outreach_sent')     return `to ${m.recipient_email || '?'} (${m.item_count ?? '?'} items)${m.channel ? ` · via ${m.channel}` : ''}`;
  if (e.event_type === 'outreach_clicked')  return `click #${m.click_count ?? '?'}`;
  if (e.event_type === 'outreach_replied')  return `reply length ${m.excerpt_length ?? '?'}`;
  return '';
}

// ===========================================================================
// Reusable bits
// ===========================================================================

function FilterChip({ label, active, onClick }) {
  const { t } = useT();
  const cls = active
    ? t('bg-blue-50 border-blue-300 text-blue-800', 'bg-blue-500/15 border-blue-500/40 text-blue-200')
    : t('bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-800', 'bg-slate-800/40 border-slate-700/40 text-slate-400 hover:border-slate-600 hover:text-slate-200');
  return (
    <button type="button" onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${cls}`}>
      {label}
    </button>
  );
}

function BrevityBadge({ score }) {
  const { t } = useT();
  if (!Number.isFinite(score)) return null;
  let cls = t('bg-slate-100 text-slate-600 border-slate-200', 'bg-slate-700/40 text-slate-300 border-slate-600/50');
  if (score >= 80) cls = t('bg-emerald-100 text-emerald-700 border-emerald-200', 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30');
  else if (score >= 60) cls = t('bg-sky-100 text-sky-700 border-sky-200', 'bg-sky-500/15 text-sky-300 border-sky-500/30');
  else if (score >= 40) cls = t('bg-amber-100 text-amber-700 border-amber-200', 'bg-amber-500/15 text-amber-300 border-amber-500/30');
  else cls = t('bg-red-100 text-red-700 border-red-200', 'bg-red-500/15 text-red-300 border-red-500/30');
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cls}`} title="Brevity score (higher = punchier)">{score}</span>;
}

function Avatar({ name }) {
  const initials = String(name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
  return (
    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500/40 to-amber-500/40 border border-orange-500/40 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
      {initials}
    </div>
  );
}

function Loader() {
  return <div className="py-12 text-center"><div className="w-8 h-8 mx-auto border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
}

function Spinner() { return <div className="inline-block w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />; }

function Empty({ title, subtitle }) {
  const { t } = useT();
  return (
    <div className={`py-12 text-center rounded-2xl border border-dashed ${t('border-slate-200', 'border-slate-700/40')}`}>
      <p className={`text-base font-medium ${t('text-slate-700', 'text-slate-300')}`}>{title}</p>
      <p className={`text-sm mt-1 ${t('text-slate-500', 'text-slate-500')}`}>{subtitle}</p>
    </div>
  );
}

function ErrorBanner({ message }) {
  const { t } = useT();
  return (
    <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${t('bg-red-50 border-red-200 text-red-700', 'bg-red-500/5 border-red-500/30 text-red-300')}`}>
      {message}
    </div>
  );
}

function DiagnosticsBar({ diagnostics }) {
  const { t } = useT();
  const failures = (diagnostics || []).filter((d) => !d.ok);
  if (failures.length === 0) return null;
  return (
    <div className={`mt-2 rounded-lg border px-3 py-2 text-[11px] ${t('bg-amber-50 border-amber-200 text-amber-700', 'bg-amber-500/5 border-amber-500/30 text-amber-300')}`}>
      Some modules couldn't be reached: {failures.map((f) => f.module).join(', ')}. Items from those modules aren't shown.
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch { return String(d); }
}

function fmtRelative(d) {
  if (!d) return '';
  const ms = Date.now() - new Date(d).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  try { return new Date(d).toLocaleDateString(); } catch { return ''; }
}

function textToBasicHtml(t) {
  if (!t) return '';
  return t.split(/\n\s*\n/).map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`).join('');
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
